import { useState } from 'react';
import SeatStrip from './SeatStrip.jsx';
import {
  formatDistance,
  formatDuration,
  formatTime,
  initials,
  shortAddress,
} from '../lib/format.js';

/** "~ 07:26" for a computed time, plain "07:26" for one the driver committed to. */
const clock = (iso, estimated) => (iso ? `${estimated ? '~ ' : ''}${formatTime(iso)}` : '');

const STOP_LABEL = {
  pickup: 'Ramassage',
  club: 'Passage au club',
  gear: 'Recuperation de materiel',
  fuel: 'Essence',
  food: 'Pause / casse-croute',
  other: 'Arret',
};

const STOP_GLYPH = { pickup: '👤', club: '🧗', gear: '🎒', fuel: '⛽', food: '☕', other: '📍' };

function Leg({ glyph, title, time, note, onLocate, last = false, dashed = false, filled = false, final = false }) {
  const inner = (
    <>
      <div className="row gap-8" style={{ alignItems: 'baseline' }}>
        <span className="leg-title truncate" style={{ flex: 1 }}>
          {glyph && <span style={{ marginRight: 5 }}>{glyph}</span>}
          {title}
        </span>
        {time && <span className="leg-time">{time}</span>}
      </div>
      {note && <div className="leg-note">{note}</div>}
    </>
  );
  return (
    <div className="leg">
      <div className="leg-rail">
        <span className={`leg-dot ${filled ? 'filled' : ''} ${final ? 'final' : ''}`} />
        {!last && <span className={`leg-line ${dashed ? 'dashed' : ''}`} />}
      </div>
      {/* A stop is a place before it is a line of text: pointing at it moves the
          map there rather than making the reader hunt for the right marker. */}
      {onLocate ? (
        <button type="button" className="leg-content leg-locate" onClick={onLocate} title="Voir sur la carte">
          {inner}
        </button>
      ) : (
        <div className="leg-content">{inner}</div>
      )}
    </div>
  );
}

export default function RideCard({
  ride,
  color,
  destination,
  selected,
  isMine,        // this browser created the ride
  canManage,     // owner, or the organiser
  myPassengerIds,
  onSelect,
  onJoin,
  onEdit,
  onDelete,
  onLeave,
  canShapeRoute,
  onResetRoute,
  onLocate,
}) {
  const { seats } = ride;
  const myPassenger = ride.passengers.find((p) => myPassengerIds.has(p.id));
  const cancelled = ride.status === 'cancelled';
  // A free seat is clickable only when this viewer could actually take it.
  const canJoin =
    !cancelled && ride.status === 'open' && seats.seatsFree > 0 && !myPassenger && !isMine;

  const [openPax, setOpenPax] = useState(null);

  /**
   * Everything actually travelling in this car.
   *
   * ride.gear is only what the driver declared. A passenger's rope is in the
   * same boot, so listing the driver's alone under "materiel dans la voiture"
   * answered a different question from the one the heading asks.
   */
  const carGear = (() => {
    const index = new Map();
    const add = (item, who) => {
      const key = String(item || '').trim();
      if (!key) return;
      if (!index.has(key)) index.set(key, []);
      if (!index.get(key).includes(who)) index.get(key).push(who);
    };
    for (const g of ride.gear ?? []) add(g, ride.driver.name);
    for (const p of ride.passengers ?? []) for (const g of p.gear ?? []) add(g, p.name);
    return [...index.entries()].sort(
      (a, b) => b[1].length - a[1].length || a[0].localeCompare(b[0], 'fr'),
    );
  })();

  const carLine = [ride.car.model, ride.car.colour].filter(Boolean).join(' · ');
  const stopNames = ride.stops.map((s) => s.place.label);

  return (
    <article
      className={`ride ${selected ? 'selected' : ''} ${cancelled ? 'cancelled' : ''}`}
      style={{ '--ride-color': color }}
    >
      <button type="button" className="ride-head" onClick={onSelect} aria-expanded={selected}>
        <span className="ride-avatar">{initials(ride.driver.name)}</span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span className="row gap-8">
            <span className="ride-name truncate" style={{ flex: 1 }}>{ride.driver.name}</span>
            <span className="ride-when">{formatTime(ride.departureTime)}</span>
          </span>
          <span className="ride-sub truncate" style={{ display: 'block' }}>
            {ride.origin.label}
            {stopNames.length > 0 && ` → ${stopNames.join(' → ')}`}
          </span>
        </span>
      </button>

      {/* Outside the button above: a free seat is itself a button, and nesting
          one inside another is neither valid nor operable. */}
      <div className="ride-seatbar row gap-8 wrap">
        <SeatStrip
          seats={seats}
          driverName={ride.driver.name}
          passengers={ride.passengers}
          color={color}
          onSeatClick={canJoin ? onJoin : undefined}
        />
        {isMine && <span className="chip chip-accent">ma voiture</span>}
        {myPassenger && !isMine && <span className="chip chip-accent">j'y suis</span>}
        {cancelled && <span className="chip chip-danger">annule</span>}
        {ride.status === 'closed' && !cancelled && <span className="chip chip-warn">ferme</span>}
      </div>

      {selected && (
        <div className="ride-body">
          {carLine && <p className="small muted" style={{ marginBottom: 10 }}>🚗 {carLine}</p>}

          <div className="section-label">Itineraire</div>
          <p className="small faint leg-hint">Touchez une etape pour la voir sur la carte.</p>
          <Leg
            filled
            glyph="🚗"
            title={ride.origin.label}
            time={formatTime(ride.departureTime)}
            note={shortAddress(ride.origin.address, ride.origin.label) || null}
            dashed={ride.stops.length === 0}
            onLocate={onLocate && (() => onLocate(ride.origin))}
          />
          {ride.stops.map((stop, i) => (
            <Leg
              key={stop.id}
              glyph={STOP_GLYPH[stop.kind]}
              onLocate={onLocate && (() => onLocate(stop.place))}
              title={stop.place.label}
              time={clock(stop.eta, stop.etaEstimated)}
              note={[
                STOP_LABEL[stop.kind],
                stop.pickupCount > 0 ? `${stop.pickupCount} personne(s)` : null,
                stop.note,
              ]
                .filter(Boolean)
                .join(' · ')}
              dashed={i === ride.stops.length - 1}
            />
          ))}
          <Leg
            final
            last
            glyph="🏔"
            onLocate={onLocate && destination ? () => onLocate(destination) : undefined}
            title={destination?.label ?? 'Destination'}
            time={clock(ride.arrival, true)}
            note={
              ride.route
                ? `${formatDistance(ride.route.distanceM)} · ${formatDuration(ride.route.durationS)} de route` +
                  (ride.via?.length ? ` · itineraire personnalise (${ride.via.length})` : '')
                : 'Itineraire en cours de calcul...'
            }
          />

          {carGear.length > 0 && (
            <>
              <div className="section-label" style={{ marginTop: 6 }}>Materiel dans la voiture</div>
              <div className="row wrap gap-6" style={{ marginBottom: 12 }}>
                {carGear.map(([item, people]) => (
                  <span key={item} className="chip" title={people.join(', ')}>
                    {item}
                    {people.length > 1 && <span className="gear-count">×{people.length}</span>}
                  </span>
                ))}
              </div>
            </>
          )}

          {canShapeRoute && (
            <p className="small faint route-hint">
              ✋ Glissez la ligne sur la carte pour faire passer votre voiture par une autre route.
            </p>
          )}

          <div className="section-label" style={{ marginTop: 6 }}>Places</div>
          <div style={{ marginBottom: 10 }}>
            <SeatStrip seats={seats} variant="summary" />
          </div>

          {ride.passengers.length === 0 ? (
            <p className="small faint" style={{ paddingBottom: 8 }}>
              Personne d'inscrit pour l'instant.
            </p>
          ) : (
            <div style={{ marginBottom: 8 }}>
              {ride.passengers.map((p) => {
                const stop = ride.stops.find((s) => s.id === p.pickupStopId);
                const mine = myPassengerIds.has(p.id);
                const open = openPax === p.id;
                const boarding = stop ? stop.place.label : 'au depart';
                return (
                  <div className={`pax ${mine ? 'is-me' : ''} ${open ? 'is-open' : ''}`} key={p.id}>
                    {/* One line never held a boarding point, a gear list and a
                        note, so it ellipsised whatever came last. It is a summary
                        now, and everything is readable one press below. */}
                    <button
                      type="button"
                      className="pax-main"
                      onClick={() => setOpenPax((cur) => (cur === p.id ? null : p.id))}
                      aria-expanded={open}
                    >
                      <span className="pax-avatar">{initials(p.name)}</span>
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span className="truncate" style={{ display: 'block', fontWeight: 550 }}>
                          {p.name}
                          {mine && <span className="faint" style={{ fontWeight: 400 }}> (vous)</span>}
                        </span>
                        <span className="small faint truncate" style={{ display: 'block' }}>
                          {stop ? `monte a ${boarding}` : 'monte au depart'}
                          {p.gear.length > 0 && ` · ${p.gear.length} objet${p.gear.length > 1 ? 's' : ''}`}
                          {p.note ? ' · note' : ''}
                        </span>
                      </span>
                      <span className="pax-chevron" aria-hidden="true">▾</span>
                    </button>
                    {(mine || canManage) && (
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => onLeave(p)}
                        title={mine ? 'Me desinscrire' : 'Retirer ce passager'}
                      >
                        ✕
                      </button>
                    )}

                    {open && (
                      <div className="pax-detail">
                        <div className="small">
                          <span className="faint">Montee : </span>
                          {stop ? boarding : 'au point de depart de la voiture'}
                        </div>
                        {p.gear.length > 0 && (
                          <div className="pax-detail-row">
                            <span className="small faint">Materiel apporte</span>
                            <span className="row wrap gap-6">
                              {p.gear.map((g) => <span key={g} className="chip chip-sm">{g}</span>)}
                            </span>
                          </div>
                        )}
                        {p.note && <div className="small pax-note">💬 {p.note}</div>}
                        {p.phone && (
                          <div className="small">
                            📞 <a href={`tel:${p.phone.replace(/\s/g, '')}`}>{p.phone}</a>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {ride.notes && (
            <p className="small muted" style={{ marginBottom: 10, paddingTop: 4 }}>💬 {ride.notes}</p>
          )}
          {ride.driver.phone && (
            <p className="small muted" style={{ marginBottom: 10 }}>
              📞 <a href={`tel:${ride.driver.phone.replace(/\s/g, '')}`}>{ride.driver.phone}</a>
            </p>
          )}

          <div className="row gap-8 wrap" style={{ marginTop: 4 }}>
            {canJoin && (
              <button type="button" className="btn btn-primary btn-sm" onClick={onJoin}>
                Prendre une place
              </button>
            )}
            {seats.seatsFree === 0 && !myPassenger && !isMine && !cancelled && (
              <span className="chip chip-warn">Voiture complete</span>
            )}
            {canManage && (
              <>
                <button type="button" className="btn btn-sm" onClick={onEdit}>Modifier</button>
                {ride.via?.length > 0 && (
                  <button type="button" className="btn btn-ghost btn-sm" onClick={onResetRoute}>
                    Itineraire direct
                  </button>
                )}
                <button type="button" className="btn btn-danger btn-sm" onClick={onDelete}>Supprimer</button>
              </>
            )}
          </div>
        </div>
      )}
    </article>
  );
}
